/*  MUI  */
import { Check, Close, OpenInNew } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    Paper,
    TableCell,
    TextField,
    Typography
} from '@mui/material';

/*  Shared  */
import { PageTitle } from 'application/Shared/Components/App';
import { AdminBackButton } from 'application/Shared/Components/Buttons/BackButton';
import { UserPicker } from 'application/Shared/Components/Pickers';
import { ManagedTable } from 'application/Shared/Components/Table';
import { TooltipQ } from 'application/Shared/Components/Tooltips';
import { Notify, Response } from 'application/Shared/Helpers';
import { Requests } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import React, { Component, useMemo } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

const headers = (t) => [
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{t('Common.name')}</strong>,
        width: '30%'
    },
    {
        align: 'left',
        field: 'default_permission',
        renderHeader: <strong>{t('Common.default_permission')}</strong>,
        width: '20%'
    },
    {
        align: 'left',
        field: 'default_new',
        renderHeader: (
            <strong>
                {t('Common.default_new_permission')}
                <TooltipQ
                    text={t('Tooltips.default_permission_new_nonstandard_user')}
                />
            </strong>
        ),
        width: '20%'
    },
    {
        align: 'left',
        field: 'users',
        renderHeader: <strong>{t('Common.users')} </strong>,
        width: '20%'
    },
    {
        align: 'right',
        field: null,
        renderHeader: <strong>{t('Common.actions')} </strong>,
        width: '10%'
    }
];

const searchFields = ['name'];

const PermissionsTable = ({ renderRow }) => {
    const { t } = useTranslation();
    const tableHeaders = useMemo(() => headers(t), [t]);

    return (
        <ManagedTable
            headers={tableHeaders}
            renderBody={renderRow}
            renderBodyKey="id"
            renderFooter
            rowsApi="/permission"
            rowsApiPreload
            rowsPerPage={10}
            searchEnabled
            searchFields={searchFields}
            sortDirection="ASC"
            sortFieldType="string"
            sortKey="name"
        />
    );
};

class Permissions extends Component {
    constructor(props) {
        super(props);
        this.state = {
            clientId: '',
            loaded: false,
            permission: {},
            rulesConfig: [],
            viewingPermission: false
        };

        /* requests */
        this.requests = new Requests();
    }

    componentDidMount() {
        const { match } = this.props;
        this.setState({ clientId: match.params.clientId }, () => {
            this.load();
        });
    }

    componentWillUnmount() {
        this.unload();
    }

    unload = () => {
        this.requests.cancel();
    };

    load = async () => {
        try {
            const response = await axios.get('/permission/config', {
                cancelToken: this.requests.token()
            });
            if (!Response.hasResponseData(response)) {
                return;
            }

            const data = Response.getResponseData(response);
            this.setState({
                loaded: true,
                rulesConfig: data
            });
        } catch (e) {
            debug.e(e);
        }
    };

    updatePermission = async () => {
        const { t } = this.props;
        const { permission, clientId } = this.state;

        const params = permission;
        if (clientId) {
            params.platformTenantId = clientId;
        }

        this.setState({ loaded: false }, async () => {
            try {
                await axios.put(`/permission/${permission.id}`, params);
                Notify.s(t('Common.permission_updated'));
            } catch (e) {
                debug.e('Unable to update permission', e);
                Notify.e(t('Common.error'));
            }
            this.setState({
                loaded: true,
                permission: {},
                viewingPermission: false
            });
        });
    };

    renderHeader = () => {
        const { fromAdminUtilities, noTitle, t } = this.props;
        const { permission } = this.state;
        const { defaultPermission } = permission;

        return (
            <>
                {!noTitle && <PageTitle title={t('Common.permissions')} />}
                {fromAdminUtilities && <AdminBackButton linkTo="/configure" />}
                {defaultPermission && (
                    <Alert severity="warning" icon={false} sx={{ mb: '16px' }}>
                        {t('Permissions.default_permissions')}
                    </Alert>
                )}
            </>
        );
    };

    renderRow = (row) => (
        <>
            <TableCell>
                <Box
                    as="a"
                    onClick={() => {
                        this.setState({
                            permission: row,
                            viewingPermission: true
                        });
                    }}
                    className="link underline"
                    role="button"
                    tabIndex={0}
                >
                    {row.name}
                </Box>
            </TableCell>
            <TableCell>
                {row.defaultPermission ? (
                    <Check sx={{ color: 'icon.success' }} />
                ) : (
                    <Close sx={{ color: 'icon.danger' }} />
                )}
            </TableCell>
            <TableCell>
                {row.defaultNewUser ? (
                    <Check sx={{ color: 'icon.success' }} />
                ) : (
                    <Close sx={{ color: 'icon.danger' }} />
                )}
            </TableCell>
            <TableCell>{row.users.length}</TableCell>
            <TableCell align="right">
                <Button
                    data-trackid="permissions_view"
                    onClick={() => {
                        this.setState({
                            permission: row,
                            viewingPermission: true
                        });
                    }}
                    role="button"
                    tabIndex={0}
                >
                    <OpenInNew sx={{ color: 'button.primary' }} />
                </Button>
            </TableCell>
        </>
    );

    renderRule = (childPrivileges) => {
        const { permission } = this.state;
        const { rules, defaultPermission } = permission;
        const { key, name } = childPrivileges;

        return (
            <React.Fragment key={key}>
                <br />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={rules.indexOf(key) > -1}
                            color="primary"
                            disabled={defaultPermission}
                            onChange={() => {
                                if (rules.indexOf(key) > -1) {
                                    rules.splice(rules.indexOf(key), 1);
                                    this.setState({
                                        permission: {
                                            ...permission,
                                            rules: [...rules]
                                        }
                                    });
                                } else {
                                    rules.push(key);
                                    this.setState({
                                        permission: {
                                            ...permission,
                                            rules: [...rules]
                                        }
                                    });
                                }
                            }}
                            required
                        />
                    }
                    label={name}
                />
            </React.Fragment>
        );
    };

    renderRules = (parentPrivileges) => {
        const { clientId, permission } = this.state;
        const { name, privileges } = parentPrivileges;

        // Need to hide these if the configure permissions page is reached at the admin hub,
        // or if the permisions are type "Client Admin".
        const permissionToHideFromClientAdmins = [
            'internal_deployment',
            'partner_sales_hub'
        ];

        const hideTitle =
            clientId &&
            permission.name === 'Client Admin' &&
            name === 'Sales Hub';

        return (
            <Grid item xs={4} key={name}>
                <hr />
                {!hideTitle && (
                    <Typography
                        variant="span"
                        color="textSecondary"
                        fontWeight={600}
                    >
                        {name}
                    </Typography>
                )}
                <br />
                {privileges.map((childPrivileges) => {
                    const hidePermissions =
                        clientId &&
                        permissionToHideFromClientAdmins.includes(
                            childPrivileges.key
                        );
                    if (hidePermissions && permission.name === 'Client Admin') {
                        return null;
                    }
                    return this.renderRule(childPrivileges);
                })}
            </Grid>
        );
    };

    renderPermissionView = () => {
        const { t } = this.props;
        const { permission, rulesConfig } = this.state;
        const { defaultPermission, name, defaultNewUser, users } = permission;

        return (
            <Grid item xs={12}>
                <Typography
                    variant="span"
                    color="textSecondary"
                    fontWeight={600}
                >
                    {t('Common.name')}
                </Typography>
                <TextField
                    fullWidth
                    disabled={defaultPermission}
                    onChange={(e) =>
                        this.setState({
                            permission: { ...permission, name: e.target.value }
                        })
                    }
                    value={name}
                />
                <br />
                <br />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={defaultNewUser}
                            color="primary"
                            onChange={(e) =>
                                this.setState({
                                    permission: {
                                        ...permission,
                                        defaultNewUser: e.target.checked
                                    }
                                })
                            }
                            required
                        />
                    }
                    label={
                        <>
                            {t('Permissions.default_new_user')}
                            <TooltipQ
                                text={t(
                                    'Tooltips.default_permission_new_nonstandard_user'
                                )}
                            />
                        </>
                    }
                />
                <br />
                <br />

                <>
                    <Typography
                        variant="span"
                        color="textSecondary"
                        fontWeight={600}
                    >
                        {t('Common.users')}
                    </Typography>
                    <UserPicker
                        multiple
                        allValue={t('Common.no_users')}
                        allValueId={undefined}
                        value={
                            Object.keys(users).length > 0 ? users : undefined
                        }
                        onChange={(e) =>
                            this.setState({
                                permission: { ...permission, users: e }
                            })
                        }
                    />
                    <br />
                    <br />
                </>

                <Grid container>
                    {rulesConfig.map((parentPrivileges) =>
                        this.renderRules(parentPrivileges)
                    )}
                </Grid>
                <hr />
                <div style={{ float: 'right' }}>
                    <Button
                        data-trackid="permissions_closeView"
                        onClick={() =>
                            this.setState({ viewingPermission: false })
                        }
                        variant="outlined"
                        color="primary"
                        style={{ marginRight: '4px' }}
                    >
                        {t('Common.close')}
                    </Button>

                    <Button
                        data-trackid="permissions_updateSave"
                        onClick={() => this.updatePermission()}
                        variant="contained"
                        color="primary"
                    >
                        {t('Common.save')}
                    </Button>
                </div>
            </Grid>
        );
    };

    render() {
        const { loaded, viewingPermission } = this.state;

        if (!loaded) {
            return this.renderHeader();
        }

        return (
            <>
                {this.renderHeader()}
                <Paper elevation={0} variant="outlined">
                    <Grid container spacing={3}>
                        {!viewingPermission && (
                            <Grid item xs={12}>
                                <PermissionsTable renderRow={this.renderRow} />
                            </Grid>
                        )}
                        {viewingPermission && this.renderPermissionView()}
                    </Grid>
                </Paper>
            </>
        );
    }
}

export default withRouter(withTranslation()(Permissions));
